import React, { useCallback, useState } from "react";
import { Theme, Typography, Box, Hidden, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useVideo } from "react-use";
import deepEqual from "react-fast-compare";
import LocalizedLink from "../../components/localized-link";
import StoryModal from "../../components/story-modal";
import VideoPlayButton from "./components/VideoPlayButton";
import BottomLeftContent from "./components/BottomLeftContent";
import TopLeftContent from "./components/TopLeftContent";
import { BackgroundLink } from "./components/BackgroundLink";
import { BackgroundVideo } from "./components/BackgroundVideo";
import {
  MOBILE_BREAKPOINT,
  DEFAULT_ASPECT_RATIO,
  DEFAULT_ASPECT_RATIO_MOBILE,
  BORDER_RADIUS,
  DEFAULT_TITLE_FONT_SIZE,
  DEFAULT_SUBTITLE_FONT_SIZE,
  BORDER_RADIUS_MOBILE
} from "./constants";

import {
  getBackgroundType,
  getLinkHref,
  getAspectRatioPaddingTop,
  getVideoMediaType,
  getVideoUrl,
  getMobileBreakpointUp,
  getThemeColor,
  getThemeColorButtonVariant,
  getMultilinesTextPerLines,
  getLinkProductSlug
} from "./utils";

import {
  CardFragment as CardFragmentData,
  CardFragment_background_DatoCmsMModuleCardBackgroundVideo as BackgroundVideoType,
  CardFragment_background as BackgroundType,
  CardFragment_background_DatoCmsMModuleCardBackgroundImage as BackgroundImageType
} from "./__generated__/CardFragment";
import CardCenteredVariant from "./components/VariantCentered";
import FadeInUp from "../../components/animations/FadeInUp";
import FullscreenVideo from "./components/FullscreenVideo";
import { Background as ImgixBackground } from "react-imgix";
import CardBottomLeftVariant from "./components/VariantBottomLeft";
import ProductDetailsModal from "../../containers/shop/ProductDetailsModal";
import { SHOP_PDP_MODAL_ENABLED } from "../../helpers/feature-flags";
import { StoryFragment as StoryType } from "../../components/story-modal/__generated__/StoryFragment";

type aspectRatioProps = {
  aspectRatio?: string;
  aspectRatioMobile?: string;
  backgroundColor?: string;
};

// Wrapper styles for both Image & Video cards
const useStyles = makeStyles<Theme, aspectRatioProps>(theme => ({
  card: {
    position: "relative",
    width: "100%",
    overflow: "hidden",
    background: ({ backgroundColor }) =>
      backgroundColor || theme.palette.grey[300],
    borderRadius: BORDER_RADIUS,
    paddingTop: ({ aspectRatio }) =>
      getAspectRatioPaddingTop(aspectRatio, DEFAULT_ASPECT_RATIO),
    "&:before": {
      borderRadius: BORDER_RADIUS
    },
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      paddingTop: ({ aspectRatioMobile }) =>
        getAspectRatioPaddingTop(
          aspectRatioMobile,
          DEFAULT_ASPECT_RATIO_MOBILE
        ),
      borderRadius: BORDER_RADIUS_MOBILE,
      "&:before": {
        borderRadius: BORDER_RADIUS_MOBILE
      }
    }
  }
}));

type Props = CardFragmentData & {
  index?: number;
  className?: string;
};

const CardInstance: React.FunctionComponent<Props> = props => {
  const isFirstContentModule = props.index === 0;
  const cardVariant = props.variant || "default";
  const background: BackgroundType = props.background[0];
  const backgroundType = getBackgroundType(background);

  const linkHref = getLinkHref(props.link);
  const ctaLinkHref = getLinkHref(props.ctaLink);

  const linkProductSlug = getLinkProductSlug(props.link);
  const ctaLinkProductSlug = getLinkProductSlug(props.ctaLink);
  const isLinkToProductDetails = !!linkProductSlug;
  const isLinkToStory = props.link?.model.apiKey === "story";
  const isCtaLinkToStory = props.ctaLink?.model.apiKey === "story";
  const isCtaLinkSameProduct = ctaLinkProductSlug === ctaLinkProductSlug;
  const [isProductDetailsModalOpen, setProductDetailsModalOpen] = useState(
    false
  );
  const [isStoryModalOpen, setStoryModalOpen] = useState(false);

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (isLinkToProductDetails) {
        e.preventDefault();
        setProductDetailsModalOpen(true);
      }
      if (isLinkToStory || isCtaLinkToStory) {
        e.preventDefault();
        setStoryModalOpen(true);
      }
    },
    [isLinkToProductDetails, isCtaLinkToStory, linkProductSlug]
  );

  const classes = useStyles({
    aspectRatio: props.aspectRatio,
    aspectRatioMobile: props.aspectRatioMobile,
    backgroundColor: props.fallbackColor ? props.fallbackColor.hex : undefined
  });

  /**
   * Wrapper changes depending on background type & card props
   * Can be a BackgroundImage or a BackgroundVideo
   */
  const CardInstanceWrapper = ({ children }) => {
    if (backgroundType === "image") {
      return (
        <ImgixBackground
          className={`${classes.card} ${props.className || ""}`}
          src={(props.background[0] as BackgroundImageType).image.url}
        >
          {children}
        </ImgixBackground>
      );
    } else if (backgroundType === "video") {
      const videoBackground = background as BackgroundVideoType;
      const { fullscreen, autoplay } = videoBackground;
      const videoUrl = getVideoUrl(videoBackground.video);

      const videoPlayButtonAnimations = {
        initial: {
          x: "-50%",
          y: "-50%",
          scale: 1.0
        },
        hover: {
          scale: 1.2
        },
        tap: {
          scale: 0.85
        }
      };

      if (fullscreen) {
        const [isFullscreenVideoOpen, setFullscreenVideoOpen] = useState(false);
        const openFullscreenVideo = useCallback(
          () => setFullscreenVideoOpen(true),
          [setFullscreenVideoOpen]
        );
        const closeFullscreenVideo = useCallback(
          () => setFullscreenVideoOpen(false),
          [setFullscreenVideoOpen]
        );

        return (
          <ImgixBackground
            className={`${classes.card} ${props.className || ""}`}
            src={(props.background[0] as BackgroundImageType).image.url}
          >
            {children}
            <VideoPlayButton
              onClick={openFullscreenVideo}
              initial="initial"
              whileHover="hover"
              whileTap="tap"
              variants={videoPlayButtonAnimations}
            />
            <FullscreenVideo
              isOpen={isFullscreenVideoOpen}
              onClose={closeFullscreenVideo}
              videoSrc={videoUrl}
              videoFallbackSrc={videoBackground.video.fallbackUrl}
            />
          </ImgixBackground>
        );
      } else {
        const [video, state, controls] = useVideo(
          <video
            key={videoBackground.video.url}
            controls={false}
            autoPlay={false}
            muted
            playsInline={!fullscreen}
            loop={!fullscreen}
            preload="metadata"
            style={{
              backgroundColor: props.fallbackColor
                ? props.fallbackColor.hex
                : undefined
            }}
          >
            <source
              src={videoUrl}
              type={getVideoMediaType(getVideoUrl(videoBackground.video))}
            />
            {videoBackground.video.fallbackUrl && (
              <source
                src={videoBackground.video.fallbackUrl}
                type={getVideoMediaType(videoBackground.video.fallbackUrl)}
              />
            )}
          </video>
        );

        const toggleVideoPlayback = (play: boolean) => {
          if (play) {
            controls.play();
          } else {
            controls.pause();
            (controls as any).currentTime = 0;
            if ((controls as any).load) {
              (controls as any).load();
            }
          }
        };

        const onPlayButtonClick = () => {
          toggleVideoPlayback(true);
        };

        return (
          <BackgroundVideo
            className={`${classes.card} ${props.className || ""}`}
            onChange={autoplay ? toggleVideoPlayback : null} // react-intersection-observer <InView onChange>
          >
            {video}
            {!autoplay && (
              <VideoPlayButton
                onClick={onPlayButtonClick}
                hideAfterClick
                initial="initial"
                whileHover="hover"
                whileTap="tap"
                variants={videoPlayButtonAnimations}
              />
            )}
            {children}
          </BackgroundVideo>
        );
      }
    }
  };

  /**
   * Animations
   */
  const backgroundAnimation =
    props.backgroundAnimation && props.backgroundAnimation !== "none"
      ? props.backgroundAnimation
      : null;
  const titleAnimation =
    props.titleAnimation && props.titleAnimation !== "none"
      ? props.titleAnimation
      : null;
  const subtitleAnimation =
    props.subtitleAnimation && props.subtitleAnimation !== "none"
      ? props.subtitleAnimation
      : null;
  const ctaAnimation =
    props.ctaAnimation && props.ctaAnimation !== "none"
      ? props.ctaAnimation
      : null;

  const baseCardDelay = 0.25;

  const titleAnimationDelay = lineIndex => {
    let delay = baseCardDelay + 0.5;
    if (props.titleAnimation === "fade-in-up-per-line") {
      delay += 0.5 * lineIndex;
    }
    return delay;
  };

  const subtitleAnimationDelay = () => {
    let delay =
      props.subtitleAnimation === "fade-in-up" ? baseCardDelay + 0.5 : 0;

    // Add extra delay to appear after title (line 1)
    if (props.titleAnimation === "fade-in-up-per-line") {
      const titleLineCount = getMultilinesTextPerLines(
        props.titleNode.childMarkdownRemark.html
      ).length;
      delay += titleLineCount * 0.5;
    } else if (props.titleAnimation === "fade-in-up") {
      delay += 1;
    }
    return delay;
  };

  const ctaAnimationDelay = () => {
    return subtitleAnimationDelay() + baseCardDelay + 0.5;
  };

  // Card wrapper + inner content
  return (
    <>
      <FadeInUp yOffset={10} disabled={!backgroundAnimation}>
        <CardInstanceWrapper>
          {linkHref && (
            <BackgroundLink
              to={linkHref}
              title={props.title}
              onClick={
                isLinkToProductDetails || isLinkToStory || isCtaLinkToStory
                  ? handleLinkClick
                  : null
              }
            />
          )}

          {["default"].includes(cardVariant) && (
            <>
              {(props.title || props.subtitle) && (
                <TopLeftContent>
                  {/* Line 1 */}
                  {props.title && !titleAnimation && (
                    <Typography
                      variant={
                        (props.titleFontSize as any) || DEFAULT_TITLE_FONT_SIZE
                      }
                      component={isFirstContentModule ? "h1" : "h2"}
                      dangerouslySetInnerHTML={{
                        __html: props.titleNode.childMarkdownRemark.html
                      }}
                      style={{
                        color: getThemeColor(props.titleTheme, "black")
                      }}
                    />
                  )}

                  {/* Line1 w/ animation */}
                  {props.title &&
                    titleAnimation?.startsWith("fade-in-up") &&
                    getMultilinesTextPerLines(
                      props.titleNode.childMarkdownRemark.html
                    ).map((line, i) => (
                      <FadeInUp key={i} delay={titleAnimationDelay(i)}>
                        <Typography
                          variant={
                            (props.titleFontSize as any) ||
                            DEFAULT_TITLE_FONT_SIZE
                          }
                          component="h2"
                          dangerouslySetInnerHTML={{
                            __html: line
                          }}
                          style={{
                            color: getThemeColor(props.titleTheme, "black")
                          }}
                        />
                      </FadeInUp>
                    ))}

                  {/* Line 2 */}
                  {props.subtitle && (
                    <Box marginTop={props.title ? 1 : 0}>
                      <FadeInUp
                        disabled={!subtitleAnimation}
                        delay={subtitleAnimationDelay()}
                      >
                        <Typography
                          variant={
                            (props.subtitleFontSize as any) ||
                            DEFAULT_SUBTITLE_FONT_SIZE
                          }
                          component={isFirstContentModule ? "h2" : "h3"}
                          dangerouslySetInnerHTML={{
                            __html: props.subtitleNode.childMarkdownRemark.html
                          }}
                          style={{
                            color: getThemeColor(props.subtitleTheme, "black")
                          }}
                        />
                      </FadeInUp>
                    </Box>
                  )}
                </TopLeftContent>
              )}
              {(props.cta || props.ctaSecondary) && (
                <BottomLeftContent>
                  <FadeInUp
                    delay={ctaAnimationDelay()}
                    disabled={!ctaAnimation}
                  >
                    {props.cta && (
                      <Button
                        variant="contained"
                        component={LocalizedLink}
                        to={ctaLinkHref}
                        title={props.cta}
                        color={getThemeColorButtonVariant(
                          props.ctaTheme,
                          "yellow"
                        )}
                        size="small"
                        onClick={
                          isCtaLinkSameProduct || isCtaLinkToStory
                            ? handleLinkClick
                            : null
                        }
                      >
                        {props.cta}
                      </Button>
                    )}
                    {props.ctaSecondary && (
                      <Button
                        variant="text"
                        component={LocalizedLink}
                        to={linkHref}
                        title={props.ctaSecondary}
                        color={getThemeColorButtonVariant(
                          props.ctaSecondaryTheme,
                          "yellow"
                        )}
                        size="small"
                        style={{
                          color: getThemeColor(
                            props.ctaSecondaryTheme,
                            "yellow"
                          )
                        }}
                      >
                        {props.ctaSecondary}
                      </Button>
                    )}
                  </FadeInUp>
                </BottomLeftContent>
              )}
            </>
          )}

          {cardVariant === "centered" && <CardCenteredVariant {...props} />}
          {cardVariant === "bottom-left" && (
            <CardBottomLeftVariant
              {...props}
              titleAnimation={titleAnimation}
              subtitleAnimation={subtitleAnimation}
              ctaAnimation={ctaAnimation}
              titleAnimationDelay={titleAnimationDelay}
              subtitleAnimationDelay={subtitleAnimationDelay}
              ctaAnimationDelay={ctaAnimationDelay}
            />
          )}
        </CardInstanceWrapper>
      </FadeInUp>

      {/* Product details modal */}
      {isLinkToProductDetails && SHOP_PDP_MODAL_ENABLED && (
        <ProductDetailsModal
          open={isProductDetailsModalOpen}
          onClose={() => setProductDetailsModalOpen(false)}
          productSelector={{
            slug: linkProductSlug
          }}
        />
      )}

      {/* Story modal */}
      {(isLinkToStory || isCtaLinkToStory) && (
        <StoryModal
          open={isStoryModalOpen}
          onClose={() => setStoryModalOpen(false)}
          story={(props.link || props.ctaLink) as StoryType}
        />
      )}
    </>
  );
};

/**
 * If the background type is different between desktop and mobile (For example video on desktop and image on mobile):
 *  we need to render two different Card instances and hide one of the other using <Hidden>
 * It would be possible to detect the current breakpoint using useMediaQuery() but it would break server side rendering:
 *  mobile device would receive desktop content on page load, then flash to mobile content after rehydration.
 */
const Card: React.FunctionComponent<Props> = props => {
  const hasMobileBackgroundOverride =
    props.backgroundMobile && props.backgroundMobile.length > 0;

  if (hasMobileBackgroundOverride) {
    const desktopOnlyProps = {
      ...props,
      backgroundMobile: null
    };
    const mobileOnlyProps = {
      ...props,
      background: props.backgroundMobile,
      backgroundMobile: null
    };

    // Generate <Hidden> props (Ex: "smDown", "mdUp") based on the MOBILE_BREAKPOINT constant
    const mobileBreakpointUp = getMobileBreakpointUp();
    const hiddenUpProps = { [`${mobileBreakpointUp}Up`]: true };
    const hiddenDownProps = { [`${MOBILE_BREAKPOINT}Down`]: true };

    return (
      <>
        <Hidden implementation="css" {...hiddenUpProps}>
          <CardInstance {...mobileOnlyProps} />
        </Hidden>
        <Hidden implementation="css" {...hiddenDownProps}>
          <CardInstance {...desktopOnlyProps} />
        </Hidden>
      </>
    );
  }

  /**
   * Else just use the same Card instance for both desktop and mobile.
   * This is possible when the card doesnt have any mobile background override or
   * when both background are just different images.
   */
  return <CardInstance {...props} />;
};

export default React.memo(Card, deepEqual);
